import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import { useAdmin } from "../../context/AdminContext";
import { ADMIN } from "../../lib/adminI18n";
import OrdersTab from "../../components/admin/OrdersTab";
import StoresTab from "../../components/admin/StoresTab";
import ProductsTab from "../../components/admin/ProductsTab";
import ChatTab from "../../components/admin/ChatTab";
import StatsTab from "../../components/admin/StatsTab";
import ImportTab from "../../components/admin/ImportTab";
import DeliveryTab from "../../components/admin/DeliveryTab";
import adminApi from "../../lib/adminApi";
import useOrderAlerts from "../../hooks/useOrderAlerts";
import { ShoppingBag, LogOut, ClipboardList, Store, Package, MessageSquare, BarChart3, Languages, ExternalLink, Loader2, Download, Truck, Bell, BellRing, Volume2, VolumeX } from "lucide-react";

const TABS = [
  { id: "orders", icon: ClipboardList, key: "tabOrders" },
  { id: "products", icon: Package, key: "tabProducts" },
  { id: "import", icon: Download, key: "tabImport" },
  { id: "delivery", icon: Truck, key: "tabDelivery" },
  { id: "stores", icon: Store, key: "tabStores" },
  { id: "chat", icon: MessageSquare, key: "tabChat" },
  { id: "stats", icon: BarChart3, key: "tabStats" },
];

export default function AdminDashboard() {
  const { lang, setLang } = useApp();
  const a = ADMIN[lang];
  const { isAuthed, user, logout, loginWithSession } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("orders");

  const { unseen, clearUnseen, soundOn, setSoundOn, notifPerm, requestNotif } = useOrderAlerts({
    enabled: isAuthed,
    labels: { newOrder: a.newOrder, newOrders: a.newOrdersN },
  });
  useEffect(() => { if (tab === "orders") clearUnseen(); }, [tab, clearUnseen]);

  const [phase, setPhase] = useState(() => (/session_id=/.test(location.hash) || new URLSearchParams(location.search).has("session_id") ? "exchanging" : "ready"));
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const hashMatch = location.hash.match(/session_id=([^&]+)/);
    const sid = hashMatch ? decodeURIComponent(hashMatch[1]) : new URLSearchParams(location.search).get("session_id");
    if (sid) {
      // Clear the hash/query immediately so a reload / back-forward can't replay a one-time session_id.
      window.history.replaceState(null, "", "/admin");
      const key = "ha_sid_done_" + sid;
      if (sessionStorage.getItem(key)) { setPhase("ready"); return; }
      sessionStorage.setItem(key, "1");
      loginWithSession(sid)
        .then(() => setPhase("ready"))
        .catch((e) => {
          let detail = e?.response?.data?.detail;
          if (!detail) {
            if (e?.response) {
              let body = "";
              try { body = typeof e.response.data === "string" ? e.response.data : JSON.stringify(e.response.data); } catch { /* ignore */ }
              detail = `HTTP ${e.response.status}${e.response.statusText ? " " + e.response.statusText : ""}${body && body !== "{}" ? " · " + body.slice(0, 140) : ""}`;
            } else {
              detail = e?.message ? String(e.message) : "Network/CORS error";
            }
          }
          // eslint-disable-next-line no-console
          console.error("[admin session exchange failed]", { status: e?.response?.status, data: e?.response?.data, message: e?.message });
          sessionStorage.setItem("ha_admin_auth_error", JSON.stringify({ authError: e.code === "not_admin" ? "not_admin" : "exchange", email: e.email || "", detail }));
          toast.error(e.code === "not_admin" ? a.notAdmin : detail);
          setPhase("ready");
        });
    } else {
      if (isAuthed) adminApi.me().catch(() => {});
      setPhase("ready");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "exchanging") {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm">{a.loggingIn}</span>
        </div>
      </div>
    );
  }

  if (!isAuthed) return <Navigate to="/admin/login" replace />;

  return (    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground shrink-0"><ShoppingBag className="h-5 w-5" /></div>
            <div className="leading-none min-w-0">
              <div className="font-head font-extrabold">{a.title}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {notifPerm !== "granted" && notifPerm !== "unsupported" && (
              <button data-testid="admin-enable-alerts" onClick={requestNotif} title={a.enableAlerts} className="h-9 px-3 rounded-lg border border-border hover:bg-accent text-sm font-semibold flex items-center gap-1.5"><Bell className="h-4 w-4" /><span className="hidden sm:inline">{a.enableAlerts}</span></button>
            )}
            {notifPerm === "granted" && (
              <span data-testid="admin-alerts-on" title={a.alertsOn} className="h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 grid place-items-center"><BellRing className="h-4 w-4" /></span>
            )}
            <button data-testid="admin-sound-toggle" onClick={() => setSoundOn(!soundOn)} title={a.sound} className="h-9 w-9 rounded-lg border border-border hover:bg-accent grid place-items-center">{soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}</button>
            <button onClick={() => setLang(lang === "sv" ? "ar" : "sv")} className="h-9 px-3 rounded-lg border border-border hover:bg-accent text-sm font-semibold flex items-center gap-1.5"><Languages className="h-4 w-4" />{lang === "sv" ? "عربي" : "SV"}</button>
            <a href="/" target="_blank" rel="noreferrer" className="h-9 px-3 rounded-lg border border-border hover:bg-accent text-sm font-semibold hidden sm:flex items-center gap-1.5"><ExternalLink className="h-4 w-4" />{a.backToSite}</a>
            <button data-testid="admin-logout-button" onClick={() => { logout(); navigate("/admin/login"); }} className="h-9 px-3 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 text-sm font-semibold flex items-center gap-1.5"><LogOut className="h-4 w-4" />{a.logout}</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.id;
            return (
              <button
                key={tb.id}
                data-testid={`admin-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`shrink-0 px-4 py-3 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <Icon className="h-4 w-4" /> {a[tb.key]}
                {tb.id === "orders" && unseen > 0 && (
                  <span data-testid="admin-orders-badge" className="ms-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">{unseen > 99 ? "99+" : unseen}</span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {tab === "orders" && <OrdersTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "import" && <ImportTab />}
        {tab === "delivery" && <DeliveryTab />}
        {tab === "stores" && <StoresTab />}
        {tab === "chat" && <ChatTab />}
        {tab === "stats" && <StatsTab />}
      </main>
    </div>
  );
}
